import { useState, useCallback } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { api, ApiError } from '@/lib/api'
import { resetChatSubscription } from '@/hooks/useChat'
import { disconnectCable } from '@/lib/actionCable'

interface UseLoginReturn {
  login: (email: string) => Promise<boolean>
  verifyOtp: (email: string, code: string) => Promise<boolean>
  isLoading: boolean
  error: string | null
  clearError: () => void
}

export function useLogin(): UseLoginReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { setUser } = useAuthStore()

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const login = useCallback(async (email: string): Promise<boolean> => {
    setIsLoading(true)
    setError(null)

    try {
      await api.auth.login(email)
      return true
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message)
      } else {
        setError('Erro ao enviar código. Tente novamente.')
      }
      return false
    } finally {
      setIsLoading(false)
    }
  }, [])

  const verifyOtp = useCallback(async (email: string, code: string): Promise<boolean> => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await api.auth.verifyOtp(email, code)
      setUser(response.user, response.token)
      // Reset chat WebSocket connection with fresh token
      resetChatSubscription()
      return true
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message)
      } else {
        setError('Erro ao verificar código. Tente novamente.')
      }
      return false
    } finally {
      setIsLoading(false)
    }
  }, [setUser])

  return {
    login,
    verifyOtp,
    isLoading,
    error,
    clearError,
  }
}

export function useLogout() {
  const { logout } = useAuthStore()
  const [isLoading, setIsLoading] = useState(false)

  const handleLogout = useCallback(async () => {
    setIsLoading(true)
    // Disconnect WebSocket before logging out
    disconnectCable()
    await logout()
    setIsLoading(false)
  }, [logout])

  return { logout: handleLogout, isLoading }
}

export function useUser() {
  const { user, isAuthenticated, isLoading } = useAuthStore()
  return { user, isAuthenticated, isLoading }
}
